import type { RoutePoint } from '../api';
import * as store from './store';

const vehicleProgress: Record<string, number> = {
  'TR-01': 0.45, 'TR-02': 0.38, 'TR-03': 0.30,
  'TR-05': 0.62, 'TR-06': 0.50, 'TR-08': 0.70,
};

const shipmentTemps: Record<string, number> = {
  'SH-2401': 4.2, 'SH-2402': 3.8, 'SH-2403': -21.3,
  'SH-2404': 3.1, 'SH-2405': 5.6, 'SH-2408': 9.2,
};

const vehicleShipment: Record<string, string> = {
  'TR-01': 'SH-2401', 'TR-02': 'SH-2404', 'TR-03': 'SH-2403',
  'TR-05': 'SH-2405', 'TR-06': 'SH-2402', 'TR-08': 'SH-2408',
};

const alertCooldowns: Record<string, number> = {};
let intervalId: number | null = null;
let tickCount = 0;

function interpolate(waypoints: RoutePoint[], progress: number): { lat: number; lng: number } {
  if (waypoints.length < 2) return { lat: waypoints[0].lat, lng: waypoints[0].lng };
  const total = waypoints.length - 1;
  const segF = progress * total;
  let seg = Math.floor(segF);
  if (seg >= waypoints.length - 1) seg = waypoints.length - 2;
  const t = segF - seg;
  return {
    lat: waypoints[seg].lat + t * (waypoints[seg + 1].lat - waypoints[seg].lat),
    lng: waypoints[seg].lng + t * (waypoints[seg + 1].lng - waypoints[seg].lng),
  };
}

function calcHeading(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const lat1R = lat1 * Math.PI / 180;
  const lat2R = lat2 * Math.PI / 180;
  const x = Math.sin(dLng) * Math.cos(lat2R);
  const y = Math.cos(lat1R) * Math.sin(lat2R) - Math.sin(lat1R) * Math.cos(lat2R) * Math.cos(dLng);
  let h = Math.atan2(x, y) * 180 / Math.PI;
  if (h < 0) h += 360;
  return h;
}

function tick() {
  tickCount++;

  const thresholds = store.getShipmentThresholds();
  const threshMap: Record<string, { min: number; max: number }> = {};
  for (const t of thresholds) threshMap[t.shipmentId] = { min: t.tempMin, max: t.tempMax };

  for (const [vid, route] of Object.entries(store.routes)) {
    let progress = vehicleProgress[vid];
    if (progress === undefined) continue;

    const old = interpolate(route, progress);
    const step = 0.002 + Math.random() * 0.002;
    progress += step;
    if (progress >= 1.0) progress = 0.0;
    vehicleProgress[vid] = progress;

    const pos = interpolate(route, progress);
    const heading = calcHeading(old.lat, old.lng, pos.lat, pos.lng);
    const speed = 65 + Math.random() * 25;
    store.updateVehiclePosition(vid, pos.lat, pos.lng, heading, speed, speed * 0.001);

    const shipId = vehicleShipment[vid];
    if (!shipId) continue;

    const baseTemp = shipmentTemps[shipId];
    let temp = baseTemp + (Math.random() - 0.5) * 1.5;
    const hum = 50 + (Math.random() - 0.5) * 10;

    if (tickCount % 80 === 0 && Math.random() < 0.4) {
      const thresh = threshMap[shipId];
      if (thresh) temp = thresh.max + 1.5 + Math.random() * 3.0;
    }

    store.updateShipmentTemp(shipId, temp, hum);

    const thresh = threshMap[shipId];
    if (thresh) {
      const key = `${vid}:${shipId}`;
      const last = alertCooldowns[key] || 0;
      const expired = Date.now() - last > 120000;

      if (temp > thresh.max && expired) {
        const sev = temp > thresh.max + 3 ? 'critical' : 'high';
        store.insertAlert(vid, shipId, 'temp_excursion', sev,
          `Temperature ${temp.toFixed(1)}°C exceeds maximum threshold of ${thresh.max.toFixed(0)}°C`);
        alertCooldowns[key] = Date.now();
      } else if (temp < thresh.min && expired) {
        const sev = temp < thresh.min - 3 ? 'critical' : 'high';
        store.insertAlert(vid, shipId, 'temp_excursion', sev,
          `Temperature ${temp.toFixed(1)}°C below minimum threshold of ${thresh.min.toFixed(0)}°C`);
        alertCooldowns[key] = Date.now();
      }
    }

    if (tickCount % 4 === 0) {
      store.insertTelemetry({
        vehicle_id: vid, shipment_id: shipId, timestamp: new Date().toISOString(),
        latitude: pos.lat, longitude: pos.lng, temperature: temp, humidity: hum,
        door_open: false, speed,
      });
    }
  }
}

export function start() {
  if (intervalId) return;
  intervalId = window.setInterval(tick, 3000);
}

export function stop() {
  if (intervalId) { clearInterval(intervalId); intervalId = null; }
}
